import {forwardRef, useMemo, useImperativeHandle} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {ButtonProps} from 'components/common/Button';
import ModalSheet from 'components/common/ModalSheet';
import StandardModalSheet from 'components/common/StandardModalSheet';
import {useTimer} from 'hooks';
import {closeSession} from 'components/sessions/utils';
import {useModalControls} from '@chekin/guestapp-ui-kit';

export type SessionCloseModalRef = {
  open: () => void;
};

type Props = {
  reservationId: string;
};
const SessionCloseModal = forwardRef<SessionCloseModalRef, Props>(
  ({reservationId}, ref) => {
    const {t} = useTranslation();
    const {isOpen, closeModal, openModal} = useModalControls();

    const {count} = useTimer({
      onFinish: () => closeSession(reservationId),
      enabled: isOpen,
    });

    const discardChangeButtons: ButtonProps[] = useMemo(() => {
      const primaryButton = {
        label: t('continue_session'),
        primary: true,
        onClick: closeModal,
      };
      const secondaryButton: ButtonProps = {
        link: true,
        label: t('end_session_now'),
        onClick: () => closeSession(reservationId),
      };
      return [primaryButton, secondaryButton];
    }, [t, closeModal, reservationId]);

    useImperativeHandle(
      ref,
      () => {
        return {
          open: openModal,
        };
      },
      [openModal],
    );

    return (
      <ModalSheet
        isOpenModalSheet={isOpen}
        handleCloseModal={closeModal}
        title={t('session_close_modal.title')}
        closeOnDocumentClick={false}
        closeButtonType="click"
      >
        <StandardModalSheet
          buttons={discardChangeButtons}
          text={
            <Trans
              i18nKey="session_close_modal.text"
              values={{
                countdown: count,
              }}
            />
          }
        />
      </ModalSheet>
    );
  },
);

export {SessionCloseModal};
